import { Textarea } from "@nextui-org/react";
import debounce from "lodash/debounce";
import { ChangeEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

const DEBOUNCE_DELAY = 300;

type EditableCardProps = {
  text: string;
  title?: string;
  onChange?: (text: string) => string | void;
  noBorder?: boolean;
  maxRows?: number;
  minRows?: number;
  isDisabled?: boolean;
  showTitle?: boolean;
  placeholder?: string;
};
const AgentEditTextField: FC<EditableCardProps> = ({
  text,
  title,
  onChange,
  noBorder,
  maxRows,
  minRows,
  isDisabled,
  showTitle = true,
  placeholder,
}) => {
  const [value, setValue] = useState(text);
  const isFirstRender = useRef(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cursorPosRef = useRef<number | null>(null);

  const placeholderString = useMemo(() => {
    return placeholder ?? "N/A";
  }, [placeholder]);

  const debouncedOnChange = useCallback(
    debounce((val: string) => {
      onChange?.(val);
    }, DEBOUNCE_DELAY),
    [onChange]
  );

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    cursorPosRef.current = e.target.selectionStart;
    debouncedOnChange(newValue);
  };

  // To Prevent textarea scrolling when user add new line
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();

      const textarea = textareaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      const newValue = value.substring(0, start) + "\n" + value.substring(end);
      setValue(newValue);

      cursorPosRef.current = start + 1;

      onChange?.(newValue);
    }
  };

  useEffect(() => {
    if (!isFirstRender.current && text !== value) {
      const textarea = textareaRef.current;
      if (textarea) {
        cursorPosRef.current = textarea.selectionStart;
      }
      setValue(text);
    }
  }, [text]);

  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea && cursorPosRef.current !== null) {
      textarea.setSelectionRange(cursorPosRef.current, cursorPosRef.current);
      cursorPosRef.current = null;
    }
  }, [value]);

  useEffect(() => {
    return () => {
      debouncedOnChange.cancel();
    };
  }, [debouncedOnChange]);

  // Check if we should show empty state
  const isEmpty = !value || value.trim() === '';
  const showEmptyState = isDisabled && isEmpty;

  return (
    <div className="relative">
      {title && showTitle && (
        <div
          className="bg-[#F9F7F7] text-[#717171] font-medium py-2 px-4 text-[10px] leading-4"
          style={{
            border: "1px solid #E4E4E7",
            borderLeft: "0px",
            borderRight: "0px",
          }}
        >
          {title}
        </div>
      )}
      {showEmptyState ? (
        <div className="border-[1px] rounded-md px-4 py-2 bg-white text-[#A1A1AA] italic text-sm">
          No data available
        </div>
      ) : (
        <Textarea
          autoFocus
          ref={textareaRef}
          className="text-[#121212] font-light bg-white"
          classNames={{
            base: "shadow-none border-none rounded-none truncate",
            inputWrapper: `border-[1px] rounded-md ${noBorder ? "border-none px-4" : ""}`,
            input: "!text-black",
          }}
          variant="bordered"
          labelPlacement="outside"
          value={value}
          placeholder={placeholderString}
          onChange={handleOnChange}
          onKeyDown={handleKeyDown}
          disabled={isDisabled}
          maxRows={maxRows}
          minRows={minRows}
        />
      )}
    </div>
  );
};

export default AgentEditTextField;
